import React, { useState, useEffect, useCallback } from 'react';
import { Button } from './ui/button';
import { Download, ChevronRight, X } from 'lucide-react';

const INSTALL_DISMISSED_KEY = 'pwa_install_dismissed';

const InstallPWA = () => {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [isIOS, setIsIOS] = useState(false);
  const [isAndroid, setIsAndroid] = useState(false);
  const [installing, setInstalling] = useState(false);

  useEffect(() => {
    // 1. Check if already running as installed PWA (standalone mode)
    const isStandalone =
      window.matchMedia('(display-mode: standalone)').matches ||
      window.navigator.standalone === true;

    if (isStandalone) return; // Already installed — never show

    // 2. Check if user previously installed or permanently dismissed
    const dismissed = localStorage.getItem(INSTALL_DISMISSED_KEY);
    if (dismissed) return;

    // 3. Detect platform
    const ua = navigator.userAgent;
    const ios = /iPad|iPhone|iPod/.test(ua) || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
    const android = /Android/i.test(ua);
    setIsIOS(ios);
    setIsAndroid(android);

    // 4. Listen for the browser's install prompt (Chrome/Edge/Samsung)
    const handleBeforeInstallPrompt = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
      setShowModal(true);
    };
    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);

    // 5. Listen for successful install → auto-dismiss and remember
    const handleAppInstalled = () => {
      localStorage.setItem(INSTALL_DISMISSED_KEY, 'installed');
      setShowModal(false);
      setDeferredPrompt(null);
    };
    window.addEventListener('appinstalled', handleAppInstalled);

    // 6. For iOS/fallback: show after delay only if beforeinstallprompt didn't fire
    let timer;
    if (ios) {
      timer = setTimeout(() => setShowModal(true), 2000);
    }

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
      window.removeEventListener('appinstalled', handleAppInstalled);
      if (timer) clearTimeout(timer);
    };
  }, []);

  const handleInstall = useCallback(async () => {
    if (!deferredPrompt) return;
    setInstalling(true);
    deferredPrompt.prompt();
    const { outcome } = await deferredPrompt.userChoice;
    if (outcome === 'accepted') {
      localStorage.setItem(INSTALL_DISMISSED_KEY, 'installed');
      setShowModal(false);
    }
    setDeferredPrompt(null);
    setInstalling(false);
  }, [deferredPrompt]);

  const handleDismiss = () => {
    // Permanently dismiss — won't show again until localStorage is cleared
    localStorage.setItem(INSTALL_DISMISSED_KEY, 'dismissed');
    setShowModal(false);
  };

  const handleSkipSession = () => {
    // Temporarily skip — will show again next browser session
    setShowModal(false);
  };

  if (!showModal) return null;

  return (
    <div className="fixed inset-0 z-[9999] bg-black/60 backdrop-blur-sm flex items-center justify-center p-4" data-testid="install-pwa-modal">
      <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full overflow-hidden">
        {/* Header with new logo */}
        <div className="bg-gradient-to-br from-blue-600 to-indigo-700 p-6 text-center text-white relative">
          <button
            onClick={handleDismiss}
            className="absolute top-3 right-3 text-white/60 hover:text-white"
            aria-label="Close"
          >
            <X className="h-5 w-5" />
          </button>
          <img
            src="/logo192.png"
            alt="EduManSystem"
            className="w-16 h-16 rounded-2xl mx-auto mb-3 shadow-lg"
          />
          <h2 className="text-xl font-bold mb-1">Install EduManSystem</h2>
          <p className="text-blue-100 text-sm">Get the best experience on your device</p>
        </div>

        <div className="p-6 space-y-4">
          <p className="text-gray-700 text-sm leading-relaxed">
            Install the app for faster access, offline support, and a native app experience.
          </p>

          <div className="space-y-2">
            {[
              'Open instantly from your home screen',
              'No need to type the URL every time',
              'Faster loading and smoother experience',
            ].map((benefit, i) => (
              <div key={i} className="flex items-start gap-2 text-sm text-gray-600">
                <ChevronRight className="h-4 w-4 text-green-500 mt-0.5 shrink-0" />
                <span>{benefit}</span>
              </div>
            ))}
          </div>

          {/* Install Action */}
          {deferredPrompt ? (
            <Button
              onClick={handleInstall}
              disabled={installing}
              className="w-full bg-blue-600 hover:bg-blue-700 h-12 text-base font-semibold"
              data-testid="install-now-btn"
            >
              <Download className="h-5 w-5 mr-2" />
              {installing ? 'Installing...' : 'Install Now'}
            </Button>
          ) : isIOS ? (
            <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 text-sm text-gray-700 space-y-2">
              <p className="font-semibold text-blue-800">To install on your iPhone/iPad:</p>
              <ol className="list-decimal ml-4 space-y-1">
                <li>Tap the <strong>Share</strong> button at the bottom of Safari</li>
                <li>Scroll down and tap <strong>&quot;Add to Home Screen&quot;</strong></li>
                <li>Tap <strong>&quot;Add&quot;</strong> to confirm</li>
              </ol>
            </div>
          ) : isAndroid ? (
            <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 text-sm text-gray-700 space-y-2">
              <p className="font-semibold text-blue-800">To install on your device:</p>
              <ol className="list-decimal ml-4 space-y-1">
                <li>Tap the <strong>three-dot menu</strong> (&#8942;) at the top-right of Chrome</li>
                <li>Tap <strong>&quot;Add to Home screen&quot;</strong> or <strong>&quot;Install app&quot;</strong></li>
                <li>Tap <strong>&quot;Install&quot;</strong> to confirm</li>
              </ol>
            </div>
          ) : (
            <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 text-sm text-gray-700 space-y-2">
              <p className="font-semibold text-blue-800">To install on your computer:</p>
              <ol className="list-decimal ml-4 space-y-1">
                <li>Click the <strong>install icon</strong> in the browser address bar</li>
                <li>Or open browser menu and select <strong>&quot;Install EduManSystem&quot;</strong></li>
              </ol>
            </div>
          )}

          <div className="flex gap-2 pt-1">
            <button
              onClick={handleSkipSession}
              className="flex-1 text-center text-xs text-gray-400 hover:text-gray-600 py-1"
            >
              Remind me later
            </button>
            <button
              onClick={handleDismiss}
              className="flex-1 text-center text-xs text-gray-400 hover:text-gray-600 py-1"
              data-testid="dont-show-again-btn"
            >
              Don&apos;t show again
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default InstallPWA;
